import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import type {
  ConversationsOutcomeInsights,
  ConversationsOutcomeWindow,
} from "../transport"
import {
  MIN_7D_CONVERSATIONS,
  MIN_24H_CONVERSATIONS,
  MIN_30D_CONVERSATIONS_FOR_DAILY_VIEW,
  MIN_30D_CONVERSATIONS_FOR_WEEKLY_VIEW,
  MS_PER_DAY,
  PACIFIC_DATE_FORMATTER,
} from "./conversationsSummaryConstants"
import type {
  ConversationsSummaryTimeframe,
  ConversationsSummaryWindowStats,
  ResolvedConversationsSummaryTimeframe,
} from "./conversationsSummaryTypes"

interface DatedConversationRow {
  row: ConversationsTableResult
  timeMs: number
}

function formatUtcDateKey(date: Date): string {
  const year = date.getUTCFullYear()
  const month = String(date.getUTCMonth() + 1).padStart(2, "0")
  const day = String(date.getUTCDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

function dateKeyToUtcNoon(dateKey: string): Date {
  const [year = 0, month = 1, day = 1] = dateKey.split("-").map(Number)

  return new Date(Date.UTC(year, month - 1, day, 12))
}

function getPacificDateKey(date: Date): string {
  const parts = Object.fromEntries(
    PACIFIC_DATE_FORMATTER.formatToParts(date).map((part) => [
      part.type,
      part.value,
    ]),
  )

  return `${parts.year}-${parts.month}-${parts.day}`
}

export function getPacificWeekStartDateKey(referenceDate: Date): string {
  const referenceDateKey = getPacificDateKey(referenceDate)
  const referenceUtcNoon = dateKeyToUtcNoon(referenceDateKey)
  const day = referenceUtcNoon.getUTCDay()
  const daysSinceMonday = day === 0 ? 6 : day - 1

  referenceUtcNoon.setUTCDate(referenceUtcNoon.getUTCDate() - daysSinceMonday)

  return formatUtcDateKey(referenceUtcNoon)
}

function parseRowTime(row: ConversationsTableResult): number | null {
  const rawDate = row.occurred_at || row.created_at
  if (!rawDate) {
    return null
  }

  const time = Date.parse(rawDate)
  return Number.isFinite(time) ? time : null
}

export function getWindowBounds(referenceDate: Date, windowDays: number) {
  const endTimeMs = referenceDate.getTime()
  return {
    endTimeMs,
    startTimeMs: endTimeMs - windowDays * MS_PER_DAY,
  }
}

export function normalizeStatus(status: string | null | undefined): string {
  return (status ?? "").replace(/_/g, " ").trim().toUpperCase()
}

function hasKnownStatus(row: ConversationsTableResult): boolean {
  return normalizeStatus(row.run_status || row.error_status).length > 0
}

function isCompletedStatus(row: ConversationsTableResult): boolean {
  const status = normalizeStatus(row.run_status)
  return status === "COMPLETED" || status === "COMPLETE" || status === "SUCCESS"
}

function isInProgressStatus(row: ConversationsTableResult): boolean {
  const status = normalizeStatus(row.run_status)
  return (
    status === "IN PROGRESS" ||
    status === "IN QUEUE" ||
    status === "PENDING" ||
    status === "QUEUED" ||
    status === "RUNNING" ||
    status === "PROCESSING"
  )
}

export function isUnsuccessfulStatus(row: ConversationsTableResult): boolean {
  const status = normalizeStatus(row.run_status || row.error_status)
  return (
    status === "FAILED" ||
    status === "FAILURE" ||
    status === "ERROR" ||
    status === "CANCELLED" ||
    status === "CANCELED" ||
    status === "TIMED OUT" ||
    status === "TIMEOUT"
  )
}

export function isMetricIssueStatus(status: string): boolean {
  const normalized = normalizeStatus(status)
  return (
    normalized.length > 0 &&
    normalized !== "COMPLETED" &&
    normalized !== "COMPLETE" &&
    normalized !== "IN PROGRESS" &&
    normalized !== "IN QUEUE" &&
    normalized !== "SUCCESS" &&
    normalized !== "PENDING" &&
    normalized !== "PASSED" &&
    normalized !== "OK" &&
    normalized !== "QUEUED" &&
    normalized !== "RUNNING" &&
    normalized !== "PROCESSING" &&
    normalized !== "CANCELLED" &&
    normalized !== "CANCELED" &&
    normalized !== "RUN CANCELLED" &&
    normalized !== "RUN CANCELED" &&
    normalized !== "SKIPPED" &&
    normalized !== "SKIP" &&
    normalized !== "NOT APPLICABLE" &&
    normalized !== "N/A" &&
    normalized !== "NA"
  )
}

export function getDatedRows(
  rows: ConversationsTableResult[],
): DatedConversationRow[] {
  return rows
    .map((row) => {
      const timeMs = parseRowTime(row)
      return timeMs === null ? null : { row, timeMs }
    })
    .filter((entry): entry is DatedConversationRow => entry !== null)
}

export function filterRowsForWindow(
  datedRows: DatedConversationRow[],
  startTimeMs: number,
  endTimeMs: number,
): ConversationsTableResult[] {
  return datedRows
    .filter((entry) => entry.timeMs >= startTimeMs && entry.timeMs < endTimeMs)
    .map((entry) => entry.row)
}

export function calculateWindowStats(
  rows: ConversationsTableResult[],
): ConversationsSummaryWindowStats {
  const statusKnownConversations = rows.filter(hasKnownStatus).length
  const completedConversations = rows.filter(isCompletedStatus).length
  const inProgressConversations = rows.filter(isInProgressStatus).length
  const unsuccessfulConversations = rows.filter(isUnsuccessfulStatus).length
  const otherStatusConversations = Math.max(
    0,
    statusKnownConversations -
      completedConversations -
      inProgressConversations -
      unsuccessfulConversations,
  )
  const finishedConversations =
    completedConversations + unsuccessfulConversations

  return {
    totalConversations: rows.length,
    statusKnownConversations,
    completedConversations,
    inProgressConversations,
    unsuccessfulConversations,
    otherStatusConversations,
    finishedConversations,
    completionRate:
      finishedConversations > 0
        ? completedConversations / finishedConversations
        : null,
  }
}

export function calculateOutcomeWindowStats(
  window: ConversationsOutcomeWindow,
): ConversationsSummaryWindowStats {
  const finishedConversations = window.completedItems + window.failedItems
  return {
    totalConversations: window.totalItems,
    statusKnownConversations: window.statusKnownItems,
    completedConversations: window.completedItems,
    inProgressConversations: window.inProgressItems,
    unsuccessfulConversations: window.failedItems,
    otherStatusConversations: Math.max(
      0,
      window.statusKnownItems -
        window.completedItems -
        window.inProgressItems -
        window.failedItems,
    ),
    finishedConversations,
    completionRate:
      finishedConversations > 0
        ? window.completedItems / finishedConversations
        : null,
  }
}

function getStatsForWindow(
  datedRows: DatedConversationRow[],
  referenceDate: Date,
  windowDays: number,
): ConversationsSummaryWindowStats {
  const bounds = getWindowBounds(referenceDate, windowDays)
  return calculateWindowStats(
    filterRowsForWindow(datedRows, bounds.startTimeMs, bounds.endTimeMs),
  )
}

export function resolveTimeframe(
  selectedTimeframe: ConversationsSummaryTimeframe,
  datedRows: DatedConversationRow[],
  referenceDate: Date,
): ResolvedConversationsSummaryTimeframe {
  if (selectedTimeframe !== "auto") {
    return selectedTimeframe
  }

  const last24hCount = getStatsForWindow(
    datedRows,
    referenceDate,
    1,
  ).totalConversations
  const last7dCount = getStatsForWindow(
    datedRows,
    referenceDate,
    7,
  ).totalConversations
  const last30dCount = getStatsForWindow(
    datedRows,
    referenceDate,
    30,
  ).totalConversations

  if (
    last24hCount >= MIN_24H_CONVERSATIONS ||
    (last30dCount >= MIN_30D_CONVERSATIONS_FOR_DAILY_VIEW && last24hCount > 0)
  ) {
    return "24h"
  }

  if (
    last7dCount >= MIN_7D_CONVERSATIONS ||
    (last30dCount >= MIN_30D_CONVERSATIONS_FOR_WEEKLY_VIEW && last7dCount > 0)
  ) {
    return "7d"
  }

  return "30d"
}

export function getTimeframeForWindowDays(
  windowDays: ConversationsOutcomeInsights["windowDays"],
): ResolvedConversationsSummaryTimeframe {
  if (windowDays === 1) {
    return "24h"
  }
  return windowDays === 7 ? "7d" : "30d"
}
